/**
 * Centralized Team Members Cache
 * 
 * Provides a shared localStorage-based cache for team members data
 * to prevent redundant API calls across the application.
 * 
 * Usage:
 *   import { fetchCachedTeamMembers, invalidateTeamMembersCache } from '@/utils/teamMembersCache';
 *   
 *   const members = await fetchCachedTeamMembers(practiceId, token);
 *   invalidateTeamMembersCache(); // Call after invite/remove
 */

import { API_BASE_URL } from '../config/api';

const CACHE_KEY_PREFIX = 'helppet_team_members_cache_';
const TTL = 15 * 60 * 1000; // 15 minutes

// Current practice being cached
let currentPracticeId: string | null = null;

// In-flight request tracking to prevent duplicate simultaneous calls
let inflightRequest: Promise<TeamMember[]> | null = null;
let inflightPracticeId: string | null = null;

function getCacheKey(practiceId: string): string {
  return `${CACHE_KEY_PREFIX}${practiceId}`;
}

export interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  role: string;
  practice_id?: string; // Optional to match other components
  photo_url?: string;
}

interface CacheData {
  members: TeamMember[];
  timestamp: number;
}

/**
 * Fetch team members with 15-minute localStorage cache
 * Returns only VET_STAFF and PRACTICE_ADMIN roles
 */
export async function fetchCachedTeamMembers(
  practiceId: string | undefined,
  token: string | null,
  options?: { forceRefresh?: boolean }
): Promise<TeamMember[]> {
  
  if (!practiceId || !token) {
    return [];
  }

  // Check if there's already an in-flight request for this practice
  if (inflightRequest && inflightPracticeId === practiceId && !options?.forceRefresh) {
    return inflightRequest;
  }

  // Check cache first (unless force refresh) - USE PRACTICE-SPECIFIC CACHE
  if (!options?.forceRefresh) {
    const cached = getCachedDataForPractice(practiceId);
    if (cached) {
      return cached.members;
    }
  }

  // Cache miss or force refresh - fetch from API
  
  // Store the promise so other simultaneous calls can wait for it
  inflightPracticeId = practiceId;
  inflightRequest = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        console.error(`Failed to fetch team members: ${response.status} ${response.statusText}`);
        return [];
      }

      const allMembers = await response.json();

      // Filter to only VET_STAFF and PRACTICE_ADMIN who are SEARCHABLE
      // is_searchable=false means they shouldn't appear in swim lanes/booking
      const vetsOnly = allMembers.filter((member: any) =>
        (member.role === 'VET_STAFF' || member.role === 'PRACTICE_ADMIN') &&
        member.is_searchable !== false  // Include if is_searchable is true OR undefined (legacy)
      );

      // Deduplicate by id
      const uniqueVets = Array.from(
        new Map(vetsOnly.map((v: any) => [v.id, v])).values()
      ) as TeamMember[];

      // Update cache with practice-specific key
      setCachedDataForPractice(practiceId, uniqueVets);

      return uniqueVets;
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      return [];
    } finally {
      // Clear in-flight tracking
      inflightRequest = null;
      inflightPracticeId = null;
    }
  })();

  return inflightRequest;
}

/**
 * Fetch ALL team members (including non-vets) - no filtering
 * Used by team management view
 */
export async function fetchAllTeamMembers(
  practiceId: string | undefined,
  token: string | null
): Promise<any[]> {
  if (!practiceId || !token) return [];

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/members`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      console.error(`Failed to fetch all team members: ${response.status} ${response.statusText}`);
      return [];
    }

    const allMembers = await response.json();

    // Also update the vet-only cache while we're at it
    // Filter by is_searchable for the operational views (swim lanes, booking)
    const vetsOnly = allMembers.filter((member: any) =>
      (member.role === 'VET_STAFF' || member.role === 'PRACTICE_ADMIN') &&
      member.is_searchable !== false  // Include if is_searchable is true OR undefined (legacy)
    );
    const uniqueVets = Array.from(
      new Map(vetsOnly.map((v: any) => [v.id, v])).values()
    ) as TeamMember[];
    setCachedDataForPractice(practiceId, uniqueVets);

    return allMembers;
  } catch (error) {
    console.error('Failed to fetch all team members:', error);
    return [];
  }
}

/**
 * Clear the team members cache for current or all practices
 * Call this after inviting/removing team members or on user login
 */
export function invalidateTeamMembersCache(practiceId?: string): void {
  try {
    if (practiceId) {
      localStorage.removeItem(getCacheKey(practiceId));
    } else {
      // Clear ALL practice-specific caches when no practiceId provided
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
    // Also clear the old cache key format for migration
    localStorage.removeItem('helppet_team_members_cache');
    currentPracticeId = null;
  } catch (error) {
    console.warn('Failed to invalidate team members cache:', error);
  }
}

/**
 * Internal: Get cached data if valid for specific practice
 */
function getCachedDataForPractice(practiceId: string): CacheData | null {
  try {
    const stored = localStorage.getItem(getCacheKey(practiceId));
    if (!stored) return null;

    const parsed: CacheData = JSON.parse(stored);
    const now = Date.now();

    // Check if cache is still fresh (within TTL)
    if (parsed.timestamp && (now - parsed.timestamp) < TTL) {
      return parsed;
    }

    return null;
  } catch (error) {
    console.warn('Failed to parse cached team members:', error);
    return null;
  }
}

/**
 * Internal: Store data in cache for specific practice
 */
function setCachedDataForPractice(practiceId: string, members: TeamMember[]): void {
  try {
    const cacheData: CacheData = {
      members,
      timestamp: Date.now()
    };
    localStorage.setItem(getCacheKey(practiceId), JSON.stringify(cacheData));
    currentPracticeId = practiceId;
  } catch (error) {
    console.warn('Failed to cache team members:', error);
  }
}

